<template>
  <b-modal
    id="pending-price-service-system-modal"
    cancel-variant="outline-secondary"
    :ok-title="$t('button.confirm')"
    :cancel-title="$t('button.close')"
    centered
    no-close-on-backdrop
    :title="$t('breadcrumb.updateServiceSystem')"
    :loading="loading"
    @ok="handleOk"
    @cancel="handleClose"
    @close="handleClose"
  >
    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        class="btn"
        @click="handleClose"
      >
        {{ $t('button.close') }}
      </b-button>
      <n-button-loading
        :loading="loading"
        class="btn"
        variant="primary"
        :disabled="isUploading"
        @submit="handleOk"
      >
        {{ $t('button.confirm') }}
      </n-button-loading>
    </template>
    <validation-observer
      ref="pendingPriceForm"
      #default="{ invalid }"
    >
      <n-form-confirmation
        ref="formConfirmation"
        key="pendingPriceForm"
        :message="
          $t('general.serviceUpdateStatusTo', {
            status: $t(`status.completed`)
          })
        "
        :confirm-button-text="`button.confirm`"
        :cancel-button-text="`button.no`"
        :form="$refs.pendingPriceForm"
        :disabled="invalid"
        @submit="submit"
      >
        <!-- Current Price Info -->
        <b-card
          v-if="showPriceCard"
          class="mb-2"
        >
          <div class="d-flex flex-column">
            <div
              v-if="getPriceTypeLabel"
              class="mb-1"
            >
              <strong>{{ $t('field.priceType') }}:</strong>
              <span class="ml-1">{{ getPriceTypeLabel }}</span>
            </div>
            <div v-if="activeChannel.servicePriceType === 1">
              <strong>{{ $t('field.price') }}:</strong>
              <span class="ml-1">{{ activeChannel.servicePriceType === 1 ? activeChannel.servicePriceInit : activeChannel.servicePrice }} {{ currency }}</span>
            </div>
            <div v-else-if="activeChannel.servicePriceType === 2">
              <strong>{{ $t('field.priceRange') }}:</strong>
              <span class="ml-1">{{ activeChannel.servicePriceMin }} - {{ activeChannel.servicePriceMax }} {{ currency }}</span>
            </div>
          </div>
        </b-card>

        <label class="d-block">
          <span>{{ $t('field.servicePrice') }}</span>
        </label>
        <div
          class="d-flex"
          style="gap: 10px"
        >
          <validation-provider
            v-slot="{ errors }"
            :rules="rules"
            :name="$t('field.servicePrice')"
            class="flex-grow-1"
          >
            <b-form-group>
              <b-form-input
                v-model="data.servicePrice"
                type="number"
                autocomplete="off"
                :placeholder="$t('field.servicePrice')"
                :name="$t('field.servicePrice')"
                :disabled="!$can('manage', 'manage-service-price')"
                :class="errors.length > 0 ? 'is-invalid' : null"
                @input="sanitizeInput"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
          <b-form-group style="min-width: 100px">
            <n-single-select
              :key="activeChannel.id"
              v-model="currency"
              :options="[
                { value: 'CNY', name: 'CNY' },
                { value: 'USD', name: 'USD' },
                { value: 'KHR', name: 'KHR' },
                { value: 'VND', name: 'VND' }
              ]"
              selection-key="value"
              selection-label="name"
              :reduce="true"
            />
          </b-form-group>
        </div>
      </n-form-confirmation>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  BFormGroup,
  BFormInput,
  BCard,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import useJwt from '@/auth/jwt/useJwt'
import NFormConfirmation from '@/components/NFormConfirmation.vue'
import NButtonLoading from '@/components/NButtonLoading.vue'
import NSingleSelect from '@/components/NSingleSelect.vue'
import ToastificationAvatar from '@core/components/toastification/ToastificationAvatar.vue'

const ServiceSystemRepository = Repository.get('serviceSystem')

export default {
  components: {
    BModal,
    BButton,
    BFormGroup,
    BFormInput,
    BCard,
    NFormConfirmation,
    NButtonLoading,
    NSingleSelect,
  },
  props: {
    activeChannel: {
      type: Object,
      required: false,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      loading: false,
      data: {
        servicePrice: null,
      },
      isUploading: false,
      currency: this.activeChannel.servicePriceCurrency,
    }
  },
  computed: {
    showPriceCard() {
      return !!(
        this.getPriceTypeLabel
      || this.activeChannel.servicePriceType === 1
      || this.activeChannel.servicePriceType === 2
      )
    },
    userData() {
      return this.$store.state.profile.email || this.$store.state.profile.phone
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
    rules() {
      return 'required'
    },
    getPriceTypeLabel() {
      const types = {
        1: this.$t('field.fixed'),
        2: this.$t('field.priceRange'),
        3: this.$t('field.negotiate'),
      }
      return types[this.activeChannel.servicePriceType]
    },
  },
  watch: {
    activeChannel: {
      handler() {
        if (this.activeChannel.servicePriceType === 1) {
          this.data.servicePrice = this.activeChannel.servicePriceInit
        }

        this.currency = this.activeChannel.servicePriceCurrency
      },
      deep: true,
    },
  },
  methods: {
    sanitizeInput(value) {
      // Remove leading zeros
      this.data.servicePrice = value.replace(/^0+(?=\d)/, '')
    },
    show() {
      if (this.activeChannel.servicePriceType !== 1) {
        this.data.servicePrice = null
      } else {
        this.data.servicePrice = this.activeChannel.servicePriceInit
      }
      this.$bvModal.show('pending-price-service-system-modal')
    },
    hide() {
      this.$bvModal.hide('pending-price-service-system-modal')
    },
    handleClose() {
      this.data.servicePrice = null
      this.hide()
    },
    handleOk(bvModal) {
      if (bvModal) bvModal.preventDefault()
      if (this.loading) return

      this.$refs.pendingPriceForm.validate().then(success => {
        if (success) {
          this.$refs.formConfirmation.confirm()
        }
      })
    },
    async submit() {
      this.loading = true

      try {
        const updateData = {
          status: 'completed',
          servicePrice: +this.data.servicePrice,
          servicePriceCurrency: this.currency,
        }

        const response = await ServiceSystemRepository.updateStatus(
          updateData,
          this.activeChannel.id,
        )

        this.$toast({
          component: ToastificationAvatar,
          position: 'top-right',
          timeout: 10000,
          props: {
            title: this.$t('alert.service'),
            icon: 'CastIcon',
            variant: 'success',
            text: this.$t('alert.serviceUpdatedSuccessfully', {
              status: this.$t('status.pendingPrice'),
            }),
          },
        })

        this.handleClose()
        this.$emit('success', response)
      } catch (error) {
        if (error.response?.status === 422) {
          this.$refs.pendingPriceForm.setErrors(
            error.response?.data?.validate,
          )
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.s-container {
  position: relative;
}

.price {
  position: absolute;
  right: 1.575rem;
  top: 3.425rem;
  padding: 0.375rem 0.75rem;
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  z-index: 10;
}
</style>
